import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react'
import axios from 'axios'
import EmotionSelectModal from './EmotionSelectModal'
import styles from './DiaryList.module.css'
import { getAccessToken } from '../Auth/AuthStore.ts'
import { AlertModal } from '../AlertModal'

const DiaryList = forwardRef(({ onNavigateToDashboard, onNavigateToDiaryWrite, onNavigateToDiaryDetail }, ref) => {
  const [diaries, setDiaries] = useState([])
  const [showEmotionModal, setShowEmotionModal] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDateForWrite, setSelectedDateForWrite] = useState(null)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  const emotionFilters = [
    { id: 'all', label: '전체' },
    { id: 'happy', label: '행복' },
    { id: 'excited', label: '기대' },
    { id: 'love', label: '사랑' },
    { id: 'calm', label: '평온' },
    { id: 'anxious', label: '걱정' },
    { id: 'proud', label: '뿌듯' },
    { id: 'tired', label: '피곤' },
    { id: 'sad', label: '슬픔' },
    { id: 'lonely', label: '외로움' },
    { id: 'angry', label: '화남' },
    { id: 'confused', label: '혼란' },
    { id: 'depressed', label: '우울' }
  ]

  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getMonthDateRange = useCallback((date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    return {
      startDate: formatDate(start),
      endDate: formatDate(end)
    }
  }, [])

  const fetchDiaries = useCallback(async (date) => {
    try {
      const token = getAccessToken()
      if (!token) {
        console.warn('로그인 토큰이 없습니다.')
        setDiaries([])
        return
      }

      const { startDate, endDate } = getMonthDateRange(date)
      const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/diaries/calendars`, {
        params: { startDate, endDate },
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      const diariesData = response.data?.data?.diaries || []
      setDiaries(diariesData)
    } catch (error) {
      console.error('일기 목록 조회에 실패했습니다.', error)
      setDiaries([])
    }
  }, [getMonthDateRange])

  useEffect(() => {
    fetchDiaries(currentDate)
  }, [currentDate, fetchDiaries])

  // 일기 목록 새로고침 함수
  const refreshDiaries = useCallback(() => {
    fetchDiaries(currentDate)
  }, [fetchDiaries, currentDate])

  const handleWriteTodayButtonClickWithDate = (selectedDate) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dateToCheck = new Date(selectedDate)
    dateToCheck.setHours(0, 0, 0, 0)
    
    // 오늘 이후 날짜인지 확인
    if (dateToCheck > today) {
      setAlertMessage('아직 일기를 작성할 수 없습니다.')
      setShowAlert(true)
      return
    }
    
    const dateStr = formatDate(dateToCheck)
    const dateDiaries = diariesByDate[dateStr]
    
    if (dateDiaries && dateDiaries.length > 0) {
      setAlertMessage('일기를 이미 작성하였습니다. 삭제 후 다시 작성해주세요.')
      setShowAlert(true)
    } else {
      setSelectedDateForWrite(dateToCheck)
      setShowEmotionModal(true)
    }
  }

  useImperativeHandle(ref, () => ({
    handleWriteTodayButtonClick,
    handleWriteTodayButtonClickWithDate,
    refreshDiaries
  }))

  const diariesByDate = useMemo(() => {
    return diaries.reduce((acc, diary) => {
      if (!diary.writtenAt) return acc
      const dateKey = diary.writtenAt.split('T')[0]
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(diary)
      return acc
    }, {})
  }, [diaries])

  // 달력 생성 함수
  const generateCalendar = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    // 이번 달 첫 날과 마지막 날
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay() // 0 (일요일) ~ 6 (토요일)
    
    const calendar = []
    
    // 이전 달의 마지막 날들 (빈 셀)
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      calendar.push({
        date: prevMonthLastDay - i,
        isCurrentMonth: false,
        isToday: false,
        hasDiary: false
      })
    }
    
    // 이번 달 날짜들
    const today = new Date()
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const diariesForDay = diariesByDate[dateStr] || []
      const hasDiary = diariesForDay.length > 0
      
      calendar.push({
        date: day,
        dateStr: dateStr,
        isCurrentMonth: true,
        isToday: today.getFullYear() === year && 
                 today.getMonth() === month && 
                 today.getDate() === day,
        hasDiary: hasDiary,
        diary: diariesForDay[0] || null,
        diaryCount: diariesForDay.length
      })
    }
    
    // 다음 달의 첫 날들 (빈 셀) - 6주를 채우기 위해
    const remainingCells = 42 - calendar.length // 6주 * 7일 = 42
    for (let day = 1; day <= remainingCells; day++) {
      calendar.push({
        date: day,
        isCurrentMonth: false,
        isToday: false,
        hasDiary: false
      })
    }
    
    return calendar
  }

  const handleDateClick = (day) => {
    if (!day.isCurrentMonth) return // 현재 달이 아니면 클릭 불가

    if (day.hasDiary && day.diary) {
      onNavigateToDiaryDetail(day.diary.diaryId)
    } else {
      // 선택한 날짜가 오늘 이후인지 확인
      const today = new Date()
      today.setHours(0, 0, 0, 0) // 시간을 00:00:00으로 설정하여 날짜만 비교
      
      const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day.date)
      selectedDate.setHours(0, 0, 0, 0)
      
      if (selectedDate > today) {
        setAlertMessage('아직 일기를 작성할 수 없습니다.')
        setShowAlert(true)
        return
      }
      
      // 해당 날짜에 일기 작성하도록
      setSelectedDateForWrite(selectedDate)
      setShowEmotionModal(true)
    }
  }

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const handleToday = () => {
    setCurrentDate(new Date())
  }

  const checkTodayDiary = () => {
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    
    const todayKey = formatDate(today)
    const todayDiaries = diariesByDate[todayKey]
    return todayDiaries?.[0]
  }

  const handleWriteTodayButtonClick = () => {
    const todayDiary = checkTodayDiary()
    if (todayDiary) {
      setAlertMessage('일기를 이미 작성하였습니다. 삭제 후 다시 작성해주세요.')
      setShowAlert(true)
    } else {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      setSelectedDateForWrite(today)
      setShowEmotionModal(true)
    }
  }

  const handleEmotionSelect = (emotion) => {
    setShowEmotionModal(false)
    // 선택한 감정과 날짜를 전달하면서 DiaryWrite로 이동
    onNavigateToDiaryWrite(emotion, selectedDateForWrite)
    setSelectedDateForWrite(null) // 날짜 초기화
  }

  const handleModalClose = () => {
    setShowEmotionModal(false)
    setSelectedDateForWrite(null) // 모달 닫을 때 선택된 날짜 초기화
  }

  const getCurrentDateString = () => {
    const date = selectedDateForWrite || new Date()
    return `${date.getMonth() + 1}/${date.getDate()} (${['일', '월', '화', '수', '목', '금', '토'][date.getDay()]})`
  }

  const calendar = generateCalendar()
  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
  const weekDays = ['일', '월', '화', '수', '목', '금', '토']

  return (
    <div className={styles.container}>
      <EmotionSelectModal
        isOpen={showEmotionModal}
        onClose={handleModalClose}
        onSelectEmotion={handleEmotionSelect}
        date={getCurrentDateString()}
      />

      <div className={styles.calendarWrapper}>
        <div className={styles.calendarHeader}>
          <button className={styles.monthNavButton} onClick={handlePrevMonth}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className={styles.monthTitle}>
            <h2>{currentDate.getFullYear()}년 {monthNames[currentDate.getMonth()]}</h2>
          </div>
          <button className={styles.monthNavButton} onClick={handleNextMonth}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className={styles.calendar}>
          <div className={styles.weekDays}>
            {weekDays.map(day => (
              <div key={day} className={styles.weekDay}>{day}</div>
            ))}
          </div>
          <div className={styles.calendarGrid}>
            {calendar.map((day, index) => {
              const diary = day.diary
              const hasImage = day.hasDiary && !!diary?.imageUrl

              return (
                <div
                  key={index}
                  className={`${styles.calendarDay} ${
                    !day.isCurrentMonth ? styles.otherMonth : ''
                  } ${day.isToday ? styles.today : ''} ${
                    day.hasDiary ? styles.hasDiary : ''
                  } ${hasImage ? styles.hasImage : ''}`}
                  onClick={() => handleDateClick(day)}
                  style={
                    hasImage
                      ? {
                          backgroundImage: `url(${diary.imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }
                      : {}
                  }
                >
                  {hasImage && <div className={styles.calendarOverlay}></div>}
                  <div className={styles.calendarContent}>
                    <span className={styles.dayNumber}>{day.date}</span>
                    {day.hasDiary && diary?.title && (
                      <div className={styles.diaryTitle}>{diary.title}</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  )
})

DiaryList.displayName = 'DiaryList'

export default DiaryList
